import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Building2 } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { ModalDialog } from '../UI/modal-dialog';
import { formControlClasses, themeClasses } from '../../config/themeClasses';
import { cn } from '../../lib/utils';

const AddToWatchlistSimpleModal = ({ company, onClose, onSubmit }) => {
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      priority: 'normal',
      alertEnabled: true,
      notes: '',
      portStartPoint: '',
      portEndPoint: '',
    },
  });

  const handleFormSubmit = async (data) => {
    setLoading(true);
    try {
      await onSubmit({
        notes: data.notes,
        priority: data.priority,
        portStartPoint: data.portStartPoint,
        portEndPoint: data.portEndPoint,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!company) return null;

  return (
    <ModalDialog
      title={t('addSupplierToWatchlistTitle')}
      description={company.name}
      onClose={onClose}
      closeLabel={t('dialogCloseLabel')}
      className="max-w-md"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            {t('cancel')}
          </Button>
          <Button type="submit" form="add-watchlist-simple-form" disabled={loading}>
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <LoadingSpinner size="sm" />
                {t('adding')}
              </span>
            ) : (
              t('addToWatchlist')
            )}
          </Button>
        </>
      }
    >
      <form id="add-watchlist-simple-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
        <div className={cn(themeClasses.appKit.emptyDashed, 'py-4 text-left')}>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary-500 shrink-0" aria-hidden />
            <div>
              <p className="text-sm font-medium text-text-primary">{company.name}</p>
              <p className="text-xs text-text-secondary">
                {company.ticker} • {company.industry}
              </p>
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="notes" className={formControlClasses.label}>
            {t('notesLabel')}
          </label>
          <textarea
            id="notes"
            {...register('notes')}
            rows={3}
            className={formControlClasses.textarea}
            placeholder={t('notesPlaceholder')}
          />
        </div>

        <div>
          <label htmlFor="priority" className={formControlClasses.label}>
            {t('priority')}
          </label>
          <select
            id="priority"
            {...register('priority', { required: t('priorityRequired') })}
            className={formControlClasses.select}
          >
            <option value="low">{t('priorityLow')}</option>
            <option value="normal">{t('priorityNormal')}</option>
            <option value="high">{t('priorityHigh')}</option>
          </select>
          {errors.priority && (
            <p className={formControlClasses.error}>{errors.priority.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="portStartPoint" className={formControlClasses.label}>
              {t('watchlistStartPoint')}
            </label>
            <Input id="portStartPoint" {...register('portStartPoint')} placeholder={t('portStartPlaceholder')} />
          </div>
          <div>
            <label htmlFor="portEndPoint" className={formControlClasses.label}>
              {t('watchlistEndPoint')}
            </label>
            <Input id="portEndPoint" {...register('portEndPoint')} placeholder={t('portEndPlaceholder')} />
          </div>
        </div>
      </form>
    </ModalDialog>
  );
};

export default AddToWatchlistSimpleModal;
